import { useMemo } from 'react'
import { useCrosswordStore } from '../store'
import { PairingListItem } from './ListItems'
import { usePlacedWordTexts, isWordPlaced } from '../hooks/usePlacedWordTexts'
import { passesUsedFilter } from '../lib/pairings'
import { passesTagFilter, type TagFilterState } from './TagFilterRow'
import { passesRatingFilter, type RatingFilterState } from './RatingFilter'
import type { ConstructorGrid, FittingPair, FittingSingleWord, Pairing, PairingStatus, ClueStatus, PreviewPlacement } from '../types'

const EMPTY_TAG_SET: ReadonlySet<string> = new Set()

type Props = {
  grid: ConstructorGrid | null
  selectedCell: { row: number; col: number } | null
  fittingPairs?: { across: FittingPair[]; down: FittingPair[] } | undefined
  fittingSingleWords?: { across: FittingSingleWord[]; down: FittingSingleWord[] } | undefined
  statusFilters?: ReadonlyArray<PairingStatus> | undefined
  clueFilters?: ReadonlyArray<ClueStatus | 'empty'> | undefined
  tagFilters?: TagFilterState | undefined
  usedTagsInGrid?: ReadonlySet<string> | undefined
  usedFilter?: 'all' | 'used' | 'unused' | undefined
  placedPairingIds?: ReadonlySet<string> | undefined
  ratingFilter?: RatingFilterState | undefined
  onPlaceSuggestion?: ((wordAText: string, wordBText: string, row: number, col: number, direction: 'across' | 'down', isTempA?: boolean, isTempB?: boolean) => void) | undefined
  onPreviewChange?: ((preview: PreviewPlacement | null) => void) | undefined
  onPairHover?: ((wordTexts: string[] | null) => void) | undefined
}

/**
 * Cell-mode suggestions surface for the assistant panel's default state.
 * When a grid cell is selected (and no pair is focused), this lists the
 * pairs / single words that fit through that cell — so the user can build
 * outwards from the grid without leaving the assistant.
 */
export function CellSuggestionsBody({
  grid,
  selectedCell,
  fittingPairs,
  fittingSingleWords,
  statusFilters,
  clueFilters,
  tagFilters,
  usedTagsInGrid,
  usedFilter,
  placedPairingIds,
  ratingFilter,
  onPlaceSuggestion,
  onPreviewChange,
  onPairHover,
}: Props) {
  const { pairings } = useCrosswordStore()
  const placedWordTexts = usePlacedWordTexts()

  const pairingsById = useMemo(() => {
    const m = new Map<string, Pairing>()
    for (const p of pairings) m.set(p.id, p)
    return m
  }, [pairings])

  if (!selectedCell) {
    return (
      <div className="px-4 py-8 text-center text-sm text-gray-400">
        Select a grid cell to see suggestions for it.
      </div>
    )
  }

  // Filter against the lifted status / clue filters. Status is the stored
  // curatorial value; "used" is a separate axis we don't filter here.
  const matchesStatus = (status: PairingStatus): boolean => {
    if (!statusFilters || statusFilters.length === 0) return status !== 'rejected'
    return statusFilters.includes(status)
  }
  const matchesClue = (clueStatus: ClueStatus | 'empty'): boolean => {
    if (!clueFilters || clueFilters.length === 0) return true
    return clueFilters.includes(clueStatus)
  }

  const usedTags = usedTagsInGrid ?? EMPTY_TAG_SET

  const filterPair = (pair: FittingPair): boolean => {
    const pairing = pair.pairingId ? pairingsById.get(pair.pairingId) ?? null : null
    const status: PairingStatus = pairing?.status ?? 'none'
    if (!matchesStatus(status)) return false
    const clueState: ClueStatus | 'empty' = pairing?.clue?.text ? pairing.clue.status : 'empty'
    if (!matchesClue(clueState)) return false
    if (tagFilters && !passesTagFilter(pairing?.tags, tagFilters, usedTags)) return false
    if (usedFilter && usedFilter !== 'all' && placedPairingIds) {
      if (!passesUsedFilter(pair.pairingId, usedFilter, placedPairingIds)) return false
    }
    if (ratingFilter && ratingFilter.threshold !== null) {
      if (!passesRatingFilter(pairing?.rating, ratingFilter)) return false
    }
    return true
  }

  // Single-word fitters use placeholders on the other side; treat status as
  // 'none' / clue as 'empty' / no tags / no rating for filter purposes.
  const filterSingleWord = (): boolean => {
    if (statusFilters && statusFilters.length > 0 && !statusFilters.includes('none')) return false
    if (clueFilters && clueFilters.length > 0 && !clueFilters.includes('empty')) return false
    // Single words have no pair → no tags. Include filters always exclude
    // them; exclude filters and used-themes can't trigger.
    if (tagFilters && tagFilters.include.length > 0) return false
    // Single-word stand-ins have no pairing record → unused. Hide when
    // filter is 'used'.
    if (usedFilter === 'used') return false
    // No pairing record → no rating. Any active rating filter excludes them.
    if (ratingFilter && ratingFilter.threshold !== null) return false
    return true
  }

  const acrossPairs = (fittingPairs?.across ?? []).filter(filterPair)
  const downPairs = (fittingPairs?.down ?? []).filter(filterPair)
  const acrossSingles = filterSingleWord() ? (fittingSingleWords?.across ?? []) : []
  const downSingles = filterSingleWord() ? (fittingSingleWords?.down ?? []) : []

  const total = acrossPairs.length + downPairs.length + acrossSingles.length + downSingles.length

  if (total === 0) {
    const emptyGrid = grid && grid.placements.length === 0
    return (
      <div className="px-4 py-8 text-center text-sm text-gray-400">
        {emptyGrid ? 'Place a word on the grid first' : 'No fitting suggestions for this cell'}
      </div>
    )
  }

  const placeholderOf = (n: number) => '□'.repeat(n)

  return (
    <div className="p-3 space-y-3">
      {(acrossPairs.length > 0 || acrossSingles.length > 0) && (
        <Section
          label="Across"
          icon="→"
          count={acrossPairs.length + acrossSingles.length}
        >
          {acrossPairs.map(pair => {
            const pairing = pair.pairingId ? pairingsById.get(pair.pairingId) : undefined
            const startRow = selectedCell.row
            const startCol = selectedCell.col - pair.startOffset
            return (
              <SuggestionRow
                key={`${pair.wordAId}:${pair.wordBId}-across-${pair.startOffset}`}
                wordAText={pair.wordAText}
                wordBText={pair.wordBText}
                row={startRow}
                col={startCol}
                direction="across"
                pairingStatus={pairing?.status}
                clueStatus={pairing?.clue?.text ? pairing.clue.status : undefined}
                clueText={pairing?.clue?.text}
                placedA={isWordPlaced(placedWordTexts, pair.wordAText)}
                placedB={isWordPlaced(placedWordTexts, pair.wordBText)}
                onPlaceSuggestion={onPlaceSuggestion}
                onPreviewChange={onPreviewChange}
                onPairHover={onPairHover}
              />
            )
          })}
          {acrossSingles.map(word => {
            const startRow = selectedCell.row
            const startCol = selectedCell.col - word.startOffset
            const letters = word.wordText.replace(/\s/g, '')
            const placeholder = placeholderOf(letters.length)
            const wordAText = word.list === 'A' ? word.wordText : placeholder
            const wordBText = word.list === 'B' ? word.wordText : placeholder
            const isTempA = word.list !== 'A'
            const isTempB = word.list !== 'B'
            return (
              <SuggestionRow
                key={`${word.wordId}:${word.wordText}:across:${word.startOffset}`}
                wordAText={wordAText}
                wordBText={wordBText}
                row={startRow}
                col={startCol}
                direction="across"
                isTempA={isTempA}
                isTempB={isTempB}
                placedA={isWordPlaced(placedWordTexts, wordAText)}
                placedB={isWordPlaced(placedWordTexts, wordBText)}
                onPlaceSuggestion={onPlaceSuggestion}
                onPreviewChange={onPreviewChange}
                onPairHover={onPairHover}
              />
            )
          })}
        </Section>
      )}

      {(downPairs.length > 0 || downSingles.length > 0) && (
        <Section
          label="Down"
          icon="↓"
          count={downPairs.length + downSingles.length}
        >
          {downPairs.map(pair => {
            const pairing = pair.pairingId ? pairingsById.get(pair.pairingId) : undefined
            const startRow = selectedCell.row - pair.startOffset
            const startCol = selectedCell.col
            return (
              <SuggestionRow
                key={`${pair.wordAId}:${pair.wordBId}-down-${pair.startOffset}`}
                wordAText={pair.wordAText}
                wordBText={pair.wordBText}
                row={startRow}
                col={startCol}
                direction="down"
                pairingStatus={pairing?.status}
                clueStatus={pairing?.clue?.text ? pairing.clue.status : undefined}
                clueText={pairing?.clue?.text}
                placedA={isWordPlaced(placedWordTexts, pair.wordAText)}
                placedB={isWordPlaced(placedWordTexts, pair.wordBText)}
                onPlaceSuggestion={onPlaceSuggestion}
                onPreviewChange={onPreviewChange}
                onPairHover={onPairHover}
              />
            )
          })}
          {downSingles.map(word => {
            const startRow = selectedCell.row - word.startOffset
            const startCol = selectedCell.col
            const letters = word.wordText.replace(/\s/g, '')
            const placeholder = placeholderOf(letters.length)
            const wordAText = word.list === 'A' ? word.wordText : placeholder
            const wordBText = word.list === 'B' ? word.wordText : placeholder
            const isTempA = word.list !== 'A'
            const isTempB = word.list !== 'B'
            return (
              <SuggestionRow
                key={`${word.wordId}:${word.wordText}:down:${word.startOffset}`}
                wordAText={wordAText}
                wordBText={wordBText}
                row={startRow}
                col={startCol}
                direction="down"
                isTempA={isTempA}
                isTempB={isTempB}
                placedA={isWordPlaced(placedWordTexts, wordAText)}
                placedB={isWordPlaced(placedWordTexts, wordBText)}
                onPlaceSuggestion={onPlaceSuggestion}
                onPreviewChange={onPreviewChange}
                onPairHover={onPairHover}
              />
            )
          })}
        </Section>
      )}
    </div>
  )
}

function Section({
  label,
  icon,
  count,
  children,
}: {
  label: string
  icon: string
  count: number
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="text-xs font-medium text-gray-500 mb-1.5 flex items-center gap-1 px-1">
        <span className="font-mono">{icon}</span> {label} ({count})
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  )
}

function SuggestionRow({
  wordAText,
  wordBText,
  row,
  col,
  direction,
  pairingStatus,
  clueStatus,
  clueText,
  isTempA,
  isTempB,
  placedA,
  placedB,
  onPlaceSuggestion,
  onPreviewChange,
  onPairHover,
}: {
  wordAText: string
  wordBText: string
  row: number
  col: number
  direction: 'across' | 'down'
  pairingStatus?: PairingStatus | undefined
  clueStatus?: ClueStatus | undefined
  clueText?: string | undefined
  isTempA?: boolean
  isTempB?: boolean
  placedA: boolean
  placedB: boolean
  onPlaceSuggestion?: ((wordAText: string, wordBText: string, row: number, col: number, direction: 'across' | 'down', isTempA?: boolean, isTempB?: boolean) => void) | undefined
  onPreviewChange?: ((preview: PreviewPlacement | null) => void) | undefined
  onPairHover?: ((wordTexts: string[] | null) => void) | undefined
}) {
  return (
    <div
      onClick={() => onPlaceSuggestion?.(wordAText, wordBText, row, col, direction, isTempA, isTempB)}
      onMouseEnter={() => {
        onPreviewChange?.({ wordAText, wordBText, row, col, direction })
        onPairHover?.([wordAText, wordBText])
      }}
      onMouseLeave={() => {
        onPreviewChange?.(null)
        onPairHover?.(null)
      }}
      className="cursor-pointer"
    >
      <PairingListItem
        textA={wordAText}
        textB={wordBText}
        autoHighlight
        isPlacedA={placedA}
        isPlacedB={placedB}
        pairingStatus={pairingStatus}
        clueStatus={clueStatus}
        clueText={clueText}
      />
    </div>
  )
}
